/**
 * The confirmation dialog and the receipt.  Masjid UX redesign (2026-10-23).
 *
 * For anything that changes a membership, a person, money, access or what the public sees:
 *  - the dialog NAMES who or what is affected, says in a short list what will happen, and asks for a reason when the
 *    caller needs one (the confirm button stays disabled, with the reason given, until it is filled);
 *  - one confirm button, which is disabled and says «Sender …» from the first click until the answer — a second click,
 *    a double-click or Enter held down cannot send twice;
 *  - modal: focus moves in, Tab stays in (lib/focus-trap), Escape and «Avbryt» close it and focus returns to what opened it;
 *  - an error from the server goes up as a toast (above the dialog, 2026-10-31); the dialog stays open, so nothing typed is lost.
 * The receipt is what stays afterwards: what happened, to whom, when, and the next step — until it is dismissed.
 * No window.confirm(): it cannot name a person in a way a screen reader announces well, and the panel tests forbid it.
 */
import React, { useEffect, useId, useRef, useState } from 'react';
import { focusables, trapStep } from '../lib/focus-trap';
import { Btn } from './ui';
import { toast } from '../lib/toast';

export type ConfirmResult = { ok: true } | { ok: false; error: string };

export function ConfirmDialog({ open, title, subject, effects, confirmLabel, danger, reason, onConfirm, onClose, children }: {
  open: boolean;
  title: string;
  /** Who or what is affected, in words: «Idris Malik · født 23.11.1985». */
  subject?: React.ReactNode;
  /** What will happen, one line each. */
  effects: React.ReactNode[];
  confirmLabel: string;
  /** Red confirm button: for what takes something away. */
  danger?: boolean;
  /** When set, a reason is asked for; `required` keeps the button disabled until it is written. */
  reason?: { label: string; required?: boolean; max?: number; hint?: string };
  /** Runs once. Returns ok, or an error sentence to show in the dialog. */
  onConfirm: (reason: string) => Promise<ConfirmResult>;
  onClose: () => void;
  children?: React.ReactNode;
}) {
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const sent = useRef(false);
  const box = useRef<HTMLDivElement>(null);
  const opener = useRef<HTMLElement | null>(null);
  const id = useId();
  useEffect(() => {
    if (!open) return;
    opener.current = document.activeElement as HTMLElement | null;
    setText(''); setBusy(false); sent.current = false;
    // Focus goes to the reason when one is asked for, otherwise to «Avbryt» — never to the button that acts.
    const t = setTimeout(() => { const f = focusables(box.current); (f.find((x) => x.tagName === 'TEXTAREA') || box.current?.querySelector<HTMLElement>('.dlg-acts .btn'))?.focus(); }, 0);
    const onKey = (e: KeyboardEvent) => {
      // Review F15: when the session runs out the sign-in overlay covers everything and this dialog becomes inert —
      // then the keys belong to the sign-in, not to a dialog nobody can reach.
      if (box.current?.closest('[inert]')) return;
      if (e.key === 'Escape' && !sent.current) { e.preventDefault(); onClose(); return; }
      if (e.key !== 'Tab') return;
      const to = trapStep(focusables(box.current), document.activeElement as HTMLElement | null, e.shiftKey);
      if (to) { e.preventDefault(); to.focus(); }
    };
    window.addEventListener('keydown', onKey);
    return () => { clearTimeout(t); window.removeEventListener('keydown', onKey); opener.current?.focus?.(); };
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!open) return null;
  const missing = reason?.required && !text.trim();
  const go = async () => {
    if (sent.current || missing) return;
    sent.current = true; setBusy(true);
    const r = await onConfirm(text.trim());
    if (r.ok) return;                 // the caller closes the dialog and shows the receipt
    sent.current = false; setBusy(false); toast(r.error, 'err');   // a toast (2026-10-31); the dialog stays open
  };
  return (
    <div className="dlg-scrim" onMouseDown={(e) => { if (e.target === e.currentTarget && !sent.current) onClose(); }}>
      <div className="dlg" role="dialog" aria-modal="true" aria-labelledby={id + '-t'} aria-describedby={id + '-d'} ref={box}>
        <h2 id={id + '-t'}>{title}</h2>
        {subject && <p className="dlg-subject">{subject}</p>}
        <div id={id + '-d'}>
          <p className="dlg-k">Dette skjer:</p>
          <ul className="dlg-effects">{effects.map((e, i) => <li key={i}>{e}</li>)}</ul>
        </div>
        {children}
        {reason && (
          <div className="field">
            <label htmlFor={id + '-r'}>{reason.label}{reason.required && <> <i>*</i></>}</label>
            <textarea id={id + '-r'} rows={3} maxLength={reason.max} value={text} aria-required={reason.required || undefined}
                      aria-describedby={reason.hint ? id + '-h' : undefined} onChange={(e) => setText(e.target.value)} />
            {reason.hint && <span className="note" id={id + '-h'}>{reason.hint}</span>}
          </div>
        )}
        <div className="dlg-acts">
          <Btn ghost small={false} onClick={onClose} disabled={busy}>Avbryt</Btn>
          <Btn small={false} danger={danger} disabled={busy || Boolean(missing)} aria-busy={busy || undefined} onClick={() => { void go(); }}>
            {busy ? 'Sender …' : confirmLabel}
          </Btn>
        </div>
        {missing && <p className="note dlg-why">Skriv en begrunnelse for å fortsette.</p>}
      </div>
    </div>
  );
}

export type ReceiptTone = 'ok' | 'wait' | 'info';
export interface ReceiptData { id: string; tone: ReceiptTone; title: string; lines?: React.ReactNode[]; action?: { label: string; href: string } }

/** A result that stays until dismissed. `role="status"`, so it is read once when it appears. */
export function Receipt({ r, onDismiss, focus }: { r: ReceiptData; onDismiss: () => void; focus?: boolean }) {
  // The newest receipt takes the focus (review F2): the page it was decided on is gone, so this is where the person is.
  const box = useRef<HTMLDivElement>(null);
  useEffect(() => { if (focus) box.current?.focus(); }, [focus]);
  return (
    <div className={'receipt ' + r.tone} role="status" tabIndex={-1} ref={box}>
      <div className="receipt-body">
        <b>{r.title}</b>
        {(r.lines || []).map((l, i) => <div key={i}>{l}</div>)}
        {r.action && <a className="receipt-link" href={r.action.href}>{r.action.label} ›</a>}
      </div>
      <button type="button" className="receipt-x" aria-label="Lukk kvitteringen" onClick={onDismiss}>×</button>
    </div>
  );
}

/** «kl. 14:05, 23.10.2026», Oslo time — for a receipt line. */
export function receiptTime(d = new Date()): string {
  const t = d.toLocaleTimeString('nb-NO', { hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Oslo' });
  const day = d.toLocaleDateString('nb-NO', { day: '2-digit', month: '2-digit', year: 'numeric', timeZone: 'Europe/Oslo' });
  return `kl. ${t}, ${day}`;
}

/**
 * The «new …» popup (owner 2026-10-23: «popup on adding or creating new, wherever we are creating»). A create form
 * no longer stands open on the page: a «+ Ny …» button opens it here, and the page keeps its list.
 *  - modal like ConfirmDialog: focus goes to the first field, Tab stays inside, focus returns to the button after;
 *  - Escape, «×» and a click on the scrim close it — but when `dirty`, they first ask «Forkaste det du har skrevet?»,
 *    so a slip of the hand never throws away a half-written form;
 *  - the form keeps its own buttons; on success the caller closes the popup and says the result ON THE PAGE, where it
 *    stays after the popup is gone; an error stays inside the popup with what was typed;
 *  - on a phone it fills the screen, and the body scrolls under a fixed title.
 */
export function FormDialog({ open, title, hint, dirty, wide, onClose, children }: {
  open: boolean;
  title: string;
  hint?: React.ReactNode;
  /** Something has been typed: closing asks before it throws that away. */
  dirty?: boolean;
  /** For forms with a preview beside them (banners, shop items). */
  wide?: boolean;
  onClose: () => void;
  children: React.ReactNode;
}) {
  const [asking, setAsking] = useState(false);
  const box = useRef<HTMLDivElement>(null);
  const opener = useRef<HTMLElement | null>(null);
  const dirtyRef = useRef(dirty); dirtyRef.current = dirty;
  const closeRef = useRef(onClose); closeRef.current = onClose;
  const id = useId();
  const tryClose = () => { if (dirtyRef.current) setAsking(true); else closeRef.current(); };
  const tryRef = useRef(tryClose); tryRef.current = tryClose;
  useEffect(() => {
    if (!open) return;
    opener.current = document.activeElement as HTMLElement | null;
    setAsking(false);
    const t = setTimeout(() => {
      // The first field; else the first button; else «×» (a popup that only explains why nothing can be made yet).
      const all = focusables(box.current), f = all.filter((x) => !x.classList.contains('fdlg-x'));
      (f.find((x) => /^(INPUT|SELECT|TEXTAREA)$/.test(x.tagName)) || f[0] || all[0])?.focus();
    }, 0);
    const onKey = (e: KeyboardEvent) => {
      if (box.current?.closest('[inert]')) return;
      if (e.key === 'Escape') { e.preventDefault(); tryRef.current(); return; }
      if (e.key !== 'Tab') return;
      const to = trapStep(focusables(box.current), document.activeElement as HTMLElement | null, e.shiftKey);
      if (to) { e.preventDefault(); to.focus(); }
    };
    window.addEventListener('keydown', onKey);
    return () => { clearTimeout(t); window.removeEventListener('keydown', onKey); opener.current?.focus?.(); };
  }, [open]);
  if (!open) return null;
  return (
    <div className="dlg-scrim fdlg-scrim" onMouseDown={(e) => { if (e.target === e.currentTarget) tryClose(); }}>
      <div className={'dlg fdlg' + (wide ? ' wide' : '')} role="dialog" aria-modal="true" aria-labelledby={id + '-t'} ref={box}>
        <div className="fdlg-head">
          <h2 id={id + '-t'}>{title}</h2>
          <button type="button" className="fdlg-x" aria-label="Lukk" onClick={tryClose}>×</button>
        </div>
        {asking && (
          <div className="fdlg-ask" role="alert">
            <span>Forkaste det du har skrevet?</span>
            <Btn ghost small={false} onClick={() => setAsking(false)}>Fortsett å skrive</Btn>
            <Btn danger small={false} onClick={() => { setAsking(false); closeRef.current(); }}>Forkast</Btn>
          </div>
        )}
        <div className="fdlg-body">
          {hint && <p className="note fdlg-hint">{hint}</p>}
          {children}
        </div>
      </div>
    </div>
  );
}
